'use client'
import React from 'react'
import { Button, Typography, Stack, Box, SxProps, Theme } from '@mui/material'
import { TypographyProps } from '@mui/material/Typography'
import { handlePhoneCall } from '../utility/helper'
import { Phone } from '@/modules/shared/ui/localComponents/svg-icons'

interface IconSizeProps {
  width?: string
  height?: string
}

type TypographyVariant = TypographyProps['variant']

interface BasePhoneDisplayProps {
  phoneNumber: string
  iconSize?: IconSizeProps
  sx?: SxProps<Theme>
  variant: 'button' | 'display'
}

interface ButtonVariantProps extends BasePhoneDisplayProps {
  variant: 'button'
  buttonVariant?: 'outlined' | 'contained' | 'text'
  backgroundColor?: string
  responsiveTypography?: boolean
  typographyVariant?: TypographyVariant
}

interface DisplayVariantProps extends BasePhoneDisplayProps {
  variant: 'display'
}

type PhoneDisplayProps = ButtonVariantProps | DisplayVariantProps

const PhoneDisplay: React.FC<PhoneDisplayProps> = (props) => {
  const {
    phoneNumber,
    variant = 'display',
    iconSize = { width: '24', height: '24' },
    sx = {},
  } = props

  if (variant === 'button') {
    const buttonProps = props as ButtonVariantProps
    const buttonVariant = buttonProps.buttonVariant || 'outlined'
    const backgroundColor = buttonProps.backgroundColor || 'transparent'
    const responsiveTypography =
      buttonProps.responsiveTypography !== undefined
        ? buttonProps.responsiveTypography
        : true
    const typographyVariant = buttonProps.typographyVariant || 'BodyLargeMedium'

    return (
      <Button
        onClick={handlePhoneCall}
        sx={{
          color: 'primaryBrandText',
          borderColor: 'primaryBrandText',
          px: 'var(--spacing__SCF_spacingS)',
          gap: 'var(--spacing__SCF_spacingXXS)',
          bgcolor:
            buttonVariant === 'contained' ? backgroundColor : 'transparent',
          display: 'flex',
          '& .MuiButton-startIcon': {
            margin: 0,
          },
          ...(sx as object),
        }}
        variant={buttonVariant}
        startIcon={<Phone width={iconSize.width} height={iconSize.height} />}
      >
        <Typography
          color='primaryBrandText'
          component='span'
          sx={
            responsiveTypography
              ? {
                  typography: {
                    xs: 'CaptionMedium',
                    md: 'BodyLargeMedium',
                  },
                }
              : undefined
          }
          variant={!responsiveTypography ? typographyVariant : undefined}
        >
          {phoneNumber}
        </Typography>
      </Button>
    )
  }

  const { ...stackRestProps } = props

  return (
    <Box
      onClick={handlePhoneCall}
      sx={{
        ...(sx as object),
      }}
    >
      <Stack
        flexDirection={'row'}
        gap='var(--spacing__SCF_spacingXS)'
        {...stackRestProps}
      >
        <Phone
          width={iconSize.width || '28'}
          height={iconSize.height || '28'}
        />
        <Typography
          color='text2'
          sx={{
            typography: { xs: 'BodyMedium', md: 'BodyLargeMedium' },
          }}
        >
          {phoneNumber}
        </Typography>
      </Stack>
    </Box>
  )
}

export default PhoneDisplay
